<script setup lang="ts">
import { storeToRefs } from 'pinia';

import Button from '@clovyr/bed/components/controls/Button.vue';
import { AuthState } from '@clovyr/pollen/pollen';
import type { PasskeyCredential } from '@clovyr/pollen/user';
import { passkeysAvailable } from '@clovyr/pollen/user';

import SettingsDetailList from '@/components/blocks/SettingsDetailList.vue';
import SettingsItem from '@/components/blocks/SettingsItem.vue';
import { usePollenStore } from '@/stores/pollen_store';

const pollenStore = usePollenStore();
const { pollen } = pollenStore;
const { authState } = storeToRefs(pollenStore);

const passkeys = ref<PasskeyCredential[]>([]);
const passkeysLoading = ref(false);
const passkeyError = ref('');
const supportsPasskeys = computed(() => passkeysAvailable());
const canEnrollPasskey = computed(() => authState.value === AuthState.LoggedIn);

async function loadPasskeys() {
  passkeysLoading.value = true;
  passkeyError.value = '';
  try {
    passkeys.value = await pollen.listPasskeys();
  } catch (e) {
    console.warn(e);
    passkeyError.value = 'Unable to load passkeys.';
  } finally {
    passkeysLoading.value = false;
  }
}

async function addPasskey() {
  passkeyError.value = '';
  if (!canEnrollPasskey.value) {
    passkeyError.value = 'Unlock your library before adding a passkey.';
    return;
  }
  try {
    await pollen.registerPasskey('Clovyr passkey');
    await loadPasskeys();
  } catch (e) {
    console.warn(e);
    passkeyError.value = 'Unable to add passkey.';
  }
}

async function renamePasskey(passkey: PasskeyCredential) {
  const nickname = window.prompt('Passkey name', passkey.nickname || 'Clovyr passkey');
  if (nickname === null) {
    return;
  }
  passkeyError.value = '';
  try {
    await pollen.renamePasskey(passkey.id, nickname);
    await loadPasskeys();
  } catch (e) {
    console.warn(e);
    passkeyError.value = 'Unable to rename passkey.';
  }
}

async function removePasskey(passkey: PasskeyCredential) {
  if (!window.confirm('Remove this passkey from your Clovyr account?')) {
    return;
  }
  passkeyError.value = '';
  try {
    await pollen.deletePasskey(passkey.id);
    await loadPasskeys();
  } catch (e) {
    console.warn(e);
    passkeyError.value = 'Unable to remove passkey.';
  }
}

onMounted(loadPasskeys);
</script>

<template>
  <SettingsItem :buttons="[]" :buttons-separator="false">
    <template #content>
      <SettingsDetailList label="Passkeys">
        <section class="passkeys" aria-label="Passkey management">
          <div class="passkeys__header">
            <div>
              <p class="passkeys__description">
                Passkeys let you sign in securely without sharing a password.
              </p>
              <p v-if="supportsPasskeys && !canEnrollPasskey" class="passkeys__notice">
                Unlock your library to add a passkey.
              </p>
            </div>
            <Button
              v-if="supportsPasskeys"
              size="small"
              label="Add passkey"
              :disabled="!canEnrollPasskey"
              @click="addPasskey"
            />
          </div>

          <p v-if="passkeyError" class="passkeys__error" role="alert">{{ passkeyError }}</p>
          <p v-if="!supportsPasskeys" class="passkeys__empty">
            This browser does not support passkeys.
          </p>
          <p v-else-if="passkeysLoading" class="passkeys__empty">Loading passkeys...</p>
          <div v-else-if="passkeys.length" class="passkeys__table-wrap">
            <table class="passkeys__table">
              <thead>
                <tr>
                  <th scope="col">Passkey</th>
                  <th scope="col">Access</th>
                  <th scope="col" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="passkey in passkeys" :key="passkey.id">
                  <td>{{ passkey.nickname || 'Clovyr passkey' }}</td>
                  <td>
                    <span
                      class="passkeys__status"
                      :class="{ 'passkeys__status--vault': passkey.vaultUnlock }"
                    >
                      {{ passkey.vaultUnlock ? 'Vault unlock' : 'Account sign-in' }}
                    </span>
                  </td>
                  <td class="passkeys__actions">
                    <div class="passkeys__action-buttons">
                      <Button size="small" label="Rename" @click="renamePasskey(passkey)" />
                      <Button
                        size="small"
                        label="Remove"
                        class="button--danger"
                        @click="removePasskey(passkey)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="passkeys__empty">No passkeys added.</p>
        </section>
      </SettingsDetailList>
    </template>
  </SettingsItem>
</template>

<style lang="scss" scoped>
.passkeys {
  display: grid;
  gap: 1rem;

  &__header {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    justify-content: space-between;

    :deep(.button) {
      flex: 0 0 auto;
      margin: 0;
    }
  }

  &__description,
  &__notice,
  &__empty,
  &__error {
    margin: 0;
  }

  &__description {
    color: #ada6b7;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  &__notice {
    margin-top: 0.375rem;
    color: #ada6b7;
    font-size: 0.75rem;
  }

  &__error {
    color: #ff8e8e;
  }

  &__table-wrap {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    min-width: 38rem;
    border-collapse: collapse;
    text-align: left;

    th,
    td {
      padding: 0.85rem 0.75rem;
      border-bottom: 1px solid var(--collection-text-translucent);
      vertical-align: middle;
    }

    th {
      font-size: 0.75rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    td:first-child {
      font-weight: 600;
    }
  }

  &__status {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--collection-text-translucent);
    font-size: 0.75rem;
    white-space: nowrap;

    &--vault {
      background: var(--collection-text-secondary);
    }
  }

  &__actions {
    width: 1%;
    white-space: nowrap;
  }

  &__action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;

    :deep(.button) {
      margin: 0;
    }
  }

  &__empty {
    color: #ada6b7;
  }
}

@media (width <= 40rem) {
  .passkeys {
    &__header {
      flex-direction: column;
    }

    &__action-buttons {
      gap: 0.25rem;
    }
  }
}
</style>
